import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { bindAll } from '@bibliocommons/utils-react';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';

import {
  bibShape,
  userIdentityShape,
  ugcCommentShape,
  ugcQuotationShape,
  ugcTagShape,
  ugcVideoShape,
  ugcSimilarTitlesShape,
  ugcPrivateNoteShape,
  ugcContentAdvisoryShape,
  ugcAgeSuitabilityShape,
  ugcSummaryShape
} from '@bibliocommons/bc-prop-types';

import { allowFreeText } from 'app/helpers/user';
import PrivateNoteDisplay from 'app/components/shared/UGC/PrivateNoteDisplay';
import shelfItemShape from 'app/shapes/shelves/shelfItemShape';
import CommentInfo from '../CommentInfo';
import QuotationsInfo from '../QuotationsInfo';
import TagsInfo from '../TagsInfo';
import VideoInfo from '../VideoInfo';
import Toolbar from '../Toolbar';
import SimilarTitlesInfo from '../SimilarTitlesInfo';
import SuitabilityInfo from '../SuitabilityInfo';
import SummaryInfo from '../SummaryInfo';

import './FooterInfo.scss';

export default class ShelfListItemFooterInfo extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);

    this.commentInfoRef = null;
    this.quotationsInfoRef = null;
    this.tagsInfoRef = null;
    this.videoInfoRef = null;

    this.state = {
      commentAdded: false,
      quotationsAdded: false,
      tagsAdded: false,
      videoAdded: false,
      similarTitlesAdded: false,
      privateNoteAdded: false,
      suitabilityInfoRef: false,
      summaryInfoRef: false
    };
  }

  static getDerivedStateFromProps(props) {
    const {
      ownerCommentForBib,
      ownerQuotationsForBib,
      ownerTagsForBib,
      ownerVideoForBib,
      ownerSimilarTitlesForBib,
      ownerPrivateNoteForBib,
      ownerAgeSuitabilityForBib,
      ownerContentAdvisoriesForBib,
      ownerSummaryForBib
    } = props;

    const tagsCount = ownerTagsForBib ? ownerTagsForBib.size : 0;
    const nextTagsCount = props.ownerTagsForBib ? props.ownerTagsForBib.size : 0;
    const quotationsCount = ownerQuotationsForBib ? ownerQuotationsForBib.size : 0;
    const nextQuotationsCount = props.ownerQuotationsForBib ? props.ownerQuotationsForBib.size : 0;
    const similarTitlesCount = ownerSimilarTitlesForBib ? ownerSimilarTitlesForBib.size : 0;
    const nextSimilarTitlesCount = props.ownerSimilarTitlesForBib ? props.ownerSimilarTitlesForBib.size : 0;

    return {
      commentAdded: props.ownerCommentForBib && !ownerCommentForBib,
      quotationsAdded: nextQuotationsCount > quotationsCount,
      tagsAdded: nextTagsCount > tagsCount,
      videoAdded: props.ownerVideoForBib && !ownerVideoForBib,
      similarTitlesAdded: nextSimilarTitlesCount > similarTitlesCount,
      privateNoteAdded: props.ownerPrivateNoteForBib && !ownerPrivateNoteForBib,
      suitabilityInfoRef:
        (props.ownerContentAdvisoriesForBib && !ownerContentAdvisoriesForBib) ||
        (props.ownerAgeSuitabilityForBib && !ownerAgeSuitabilityForBib),
      summaryInfoRef: props.ownerSummaryForBib && !ownerSummaryForBib
    };
  }

  handleUGCFocus(previouslyFocusedElement) {
    const {
      commentAdded,
      quotationsAdded,
      tagsAdded,
      videoAdded,
      similarTitlesAdded,
      privateNoteAdded,
      suitabilityInfoRef,
      summaryInfoRef
    } = this.state;

    setTimeout(() => {
      if (commentAdded && this.commentInfoRef) {
        return setFocusWithinElement(this.commentInfoRef);
      }

      if (quotationsAdded && this.quotationsInfoRef) {
        return setFocusWithinElement(this.quotationsInfoRef);
      }

      if (tagsAdded && this.tagsInfoRef) {
        return setFocusWithinElement(this.tagsInfoRef);
      }

      if (videoAdded && this.videoInfoRef) {
        return setFocusWithinElement(this.videoInfoRef);
      }

      if (similarTitlesAdded && this.similarTitlesInfoRef) {
        return setFocusWithinElement(this.similarTitlesInfoRef);
      }

      if (privateNoteAdded && this.privateNoteInfoRef) {
        return setFocusWithinElement(this.privateNoteInfoRef);
      }

      if (suitabilityInfoRef && this.suitabilityInfoRef) {
        return setFocusWithinElement(this.suitabilityInfoRef);
      }

      if (summaryInfoRef && this.summaryInfoRef) {
        return setFocusWithinElement(this.summaryInfoRef);
      }

      return previouslyFocusedElement.focus();
    }, 0);
  }

  renderToolbar() {
    const { owner } = this.props;
    if (owner.get('loggedIn') && allowFreeText(owner)) {
      return (
        <Toolbar
          bib={this.props.bib}
          item={this.props.item}
          owner={this.props.owner}
          ownerCommentForBib={this.props.ownerCommentForBib}
          ownerTagsForBib={this.props.ownerTagsForBib}
          ownerVideoForBib={this.props.ownerVideoForBib}
          similarTitleBibs={this.props.similarTitleBibs}
          ownerPrivateNoteForBib={this.props.ownerPrivateNoteForBib}
          ownerContentAdvisoriesForBib={this.props.ownerContentAdvisoriesForBib}
          ownerAgeSuitabilityForBib={this.props.ownerAgeSuitabilityForBib}
          ownerSummaryForBib={this.props.ownerSummaryForBib}
          ownerQuotationsForBib={this.props.ownerQuotationsForBib}
          commentsTransactions={this.props.commentsTransactions}
          quotationsTransactions={this.props.quotationsTransactions}
          shelfTransactions={this.props.shelfTransactions}
          tagsTransactions={this.props.tagsTransactions}
          videosTransactions={this.props.videosTransactions}
          similarTitlesTransactions={this.props.similarTitlesTransactions}
          privateNotesTransactions={this.props.privateNotesTransactions}
          contentAdvisoriesTransactions={this.props.contentAdvisoriesTransactions}
          ageSuitabilitiesTransactions={this.props.ageSuitabilitiesTransactions}
          summariesTransactions={this.props.summariesTransactions}
          commentsActions={this.props.commentsActions}
          quotationsActions={this.props.quotationsActions}
          shelfActions={this.props.shelfActions}
          tagsActions={this.props.tagsActions}
          videosActions={this.props.videosActions}
          similarTitlesActions={this.props.similarTitlesActions}
          privateNotesActions={this.props.privateNotesActions}
          contentAdvisoriesActions={this.props.contentAdvisoriesActions}
          ageSuitabilitiesActions={this.props.ageSuitabilitiesActions}
          summariesActions={this.props.summariesActions}
          handleUGCFocus={this.handleUGCFocus}
        />
      );
    }

    return null;
  }

  render() {
    const {
      bib,
      owner,
      item,
      ownerCommentForBib,
      ownerQuotationsForBib,
      ownerTagsForBib,
      ownerVideoForBib,
      ownerSimilarTitlesForBib,
      ownerPrivateNoteForBib,
      similarTitleBibs,
      commentsTransactions,
      quotationsTransactions,
      shelfTransactions,
      tagsTransactions,
      videosTransactions,
      similarTitlesTransactions,
      privateNotesTransactions,
      ownerContentAdvisoriesForBib,
      ownerAgeSuitabilityForBib,
      ownerSummaryForBib,
      contentAdvisoriesTransactions,
      ageSuitabilitiesTransactions,
      summariesTransactions,
      commentsActions,
      quotationsActions,
      shelfActions,
      tagsActions,
      videosActions,
      similarTitlesActions,
      privateNotesActions,
      contentAdvisoriesActions,
      ageSuitabilitiesActions,
      summariesActions
    } = this.props;

    return (
      <div className="cp-shelf-list-item-footer-info">
        <div className="ugc-info">
          <CommentInfo
            bib={bib}
            item={item}
            owner={owner}
            ownerCommentForBib={ownerCommentForBib}
            commentsTransactions={commentsTransactions}
            shelfTransactions={shelfTransactions}
            commentsActions={commentsActions}
            shelfActions={shelfActions}
            ref={el => {
              this.commentInfoRef = el;
            }}
          />
          <TagsInfo
            bib={bib}
            owner={owner}
            ownerTagsForBib={ownerTagsForBib}
            tagsTransactions={tagsTransactions}
            tagsActions={tagsActions}
            ref={el => {
              this.tagsInfoRef = el;
            }}
          />
          <QuotationsInfo
            bib={bib}
            item={item}
            owner={owner}
            ownerQuotationsForBib={ownerQuotationsForBib}
            quotationsTransactions={quotationsTransactions}
            shelfTransactions={shelfTransactions}
            quotationsActions={quotationsActions}
            shelfActions={shelfActions}
            ref={el => {
              this.quotationsInfoRef = el;
            }}
          />
          <SummaryInfo
            bib={bib}
            item={item}
            owner={owner}
            ownerSummaryForBib={ownerSummaryForBib}
            summariesActions={summariesActions}
            summariesTransactions={summariesTransactions}
            shelfActions={shelfActions}
            shelfTransactions={shelfTransactions}
            ref={el => {
              this.summaryInfoRef = el;
            }}
          />
          <VideoInfo
            bib={bib}
            item={item}
            owner={owner}
            ownerVideoForBib={ownerVideoForBib}
            shelfTransactions={shelfTransactions}
            videosActions={videosActions}
            videosTransactions={videosTransactions}
            shelfActions={shelfActions}
            ref={el => {
              this.videoInfoRef = el;
            }}
          />
          <SimilarTitlesInfo
            bib={bib}
            item={item}
            owner={owner}
            similarTitleBibs={similarTitleBibs}
            ownerSimilarTitlesForBib={ownerSimilarTitlesForBib}
            similarTitlesActions={similarTitlesActions}
            similarTitlesTransactions={similarTitlesTransactions}
            shelfActions={shelfActions}
            shelfTransactions={shelfTransactions}
            ref={el => {
              this.similarTitlesInfoRef = el;
            }}
          />
          <PrivateNoteDisplay
            bib={bib}
            shelfItem={item}
            owner={owner}
            privateNote={ownerPrivateNoteForBib}
            privateNotesTransactions={privateNotesTransactions}
            shelfTransactions={shelfTransactions}
            privateNotesActions={privateNotesActions}
            shelfActions={shelfActions}
            ref={el => {
              this.privateNoteInfoRef = el;
            }}
          />
          <SuitabilityInfo
            bib={bib}
            item={item}
            owner={owner}
            ownerContentAdvisoriesForBib={ownerContentAdvisoriesForBib}
            ownerAgeSuitabilityForBib={ownerAgeSuitabilityForBib}
            contentAdvisoriesActions={contentAdvisoriesActions}
            ageSuitabilitiesActions={ageSuitabilitiesActions}
            contentAdvisoriesTransactions={contentAdvisoriesTransactions}
            ageSuitabilitiesTransactions={ageSuitabilitiesTransactions}
            shelfActions={shelfActions}
            shelfTransactions={shelfTransactions}
            ref={el => {
              this.suitabilityInfoRef = el;
            }}
          />
        </div>
        {this.renderToolbar()}
      </div>
    );
  }
}

ShelfListItemFooterInfo.propTypes = {
  bib: bibShape.isRequired,
  owner: userIdentityShape.isRequired,
  item: shelfItemShape.isRequired,
  ownerCommentForBib: ugcCommentShape,
  ownerQuotationsForBib: ImmutablePropTypes.listOf(ugcQuotationShape),
  ownerVideoForBib: ugcVideoShape,
  ownerSimilarTitlesForBib: ImmutablePropTypes.listOf(ugcSimilarTitlesShape),
  similarTitleBibs: ImmutablePropTypes.map,
  ownerTagsForBib: ImmutablePropTypes.listOf(ugcTagShape),
  ownerPrivateNoteForBib: ugcPrivateNoteShape,
  ownerContentAdvisoriesForBib: ImmutablePropTypes.listOf(ugcContentAdvisoryShape),
  ownerAgeSuitabilityForBib: ugcAgeSuitabilityShape,
  ownerSummaryForBib: ugcSummaryShape,
  commentsTransactions: ImmutablePropTypes.map.isRequired,
  quotationsTransactions: ImmutablePropTypes.map.isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  tagsTransactions: ImmutablePropTypes.map.isRequired,
  videosTransactions: ImmutablePropTypes.map.isRequired,
  similarTitlesTransactions: ImmutablePropTypes.map.isRequired,
  privateNotesTransactions: ImmutablePropTypes.map.isRequired,
  contentAdvisoriesTransactions: ImmutablePropTypes.map.isRequired,
  ageSuitabilitiesTransactions: ImmutablePropTypes.map.isRequired,
  summariesTransactions: ImmutablePropTypes.map.isRequired,
  commentsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  quotationsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  tagsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  videosActions: PropTypes.objectOf(PropTypes.func).isRequired,
  similarTitlesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  privateNotesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  contentAdvisoriesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  ageSuitabilitiesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  summariesActions: PropTypes.objectOf(PropTypes.func).isRequired
};
